import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UiTagComponent } from '../../../../../shared/components/ui-tag/ui-tag.component';
import { normalizarValorOrdenacao } from '@shared/helpers/ordenacao-tabela.helper';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { TipoEtapaKanbanEnum } from '../../../../../shared/enums/vendas/tipo-etapa-kanban.enum';
import { VendaTipoDocumentoEnum } from '../../../../../shared/enums/vendas/venda-tipo-documento.enum';
import { VendaTipoIntegracaoEnum, VendaTipoIntegracaoIndicador } from '@shared/enums/vendas/venda-tipo-integracao.enum';
import { VendaDocumentoStruct } from '../../../../../shared/services/structs/vendas/venda-documento.struct';
import {
    acoesOportunidade,
    formatarNumeroDocumento,
    PvAcoesOportunidadeVm,
    PvLinhaListaVm,
    PvPermissoesOportunidade,
    temMarcaOportunidade,
    TOOLTIP_OPORTUNIDADE_OUTRO_VENDEDOR,
    tooltipMarcaOportunidade,
} from '../../processo-venda.models';

/**
 * Visão em lista do Processo de Venda, no mesmo formato das outras listagens do HUB
 * (serviços tomados, contas a pagar, produtos): mat-table dentro de .table-container
 * .desktop-view, e .card-mobile na .mobile-view.
 *
 * Clique na linha NÃO abre o registro — a story é explícita: a abertura passa pelo menu de
 * ações. O menu é o MESMO do card do board, com os mesmos itens e as mesmas regras: a
 * Lista é outra visão dos mesmos documentos, não outro conjunto de ações.
 */
@Component({
    selector: 'app-pv-lista',
    standalone: true,
    imports: [
        CommonModule,
        MatTableModule,
        MatCheckboxModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatDividerModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatSortModule,
        UiTagComponent,
        AbntPipe,
    ],
    templateUrl: './pv-lista.component.html',
    styleUrls: ['./pv-lista.component.css'],
})
export class PvListaComponent {
    @Input() set linhas(valor: PvLinhaListaVm[]) {
        this._dataSource.data = valor ?? [];
    }

    /** A seleção mora no shell: o botão "Ações (n)" do cabeçalho precisa dela. */
    @Input({ required: true }) listSelection: SelectionModel<PvLinhaListaVm>;

    /** Não há mais páginas a buscar — desabilita o "Ver Mais". */
    @Input() isUltimaPagina = false;

    /** Buscando a próxima página: o spinner vai DENTRO do botão, não sobre a tabela. */
    @Input() isCarregandoMais = false;

    /** Sem permissão de inserir, "Criar modelo" fica desabilitado no menu da linha. */
    @Input() podeInserir = false;

    /** Sem permissão de excluir, "Excluir" fica desabilitado no menu da linha. */
    @Input() podeExcluir = false;

    /** Permissões das ações de oportunidade (RX-04, RX-05), resolvidas uma vez pelo shell. */
    @Input({ required: true }) permissoesOportunidade: PvPermissoesOportunidade;
    /** "Lançar financeiro" (pedido sem financeiro): a permissão de inserir. */
    @Input() podeLancarFinanceiro = false;

    /** "Excluir financeiro" (pedido com financeiro): Vendas_ProcessoVenda_ExcluirFinanceiro. */
    @Input() podeExcluirFinanceiro = false;

    /** "Lançar estoque" (pedido sem estoque): a permissão de inserir. */
    @Input() podeLancarEstoque = false;

    /** "Excluir estoque" (pedido com estoque): Vendas_ProcessoVenda_ExcluirEstoque. */
    @Input() podeExcluirEstoque = false;

    @Output() alternarTodos = new EventEmitter<void>();
    @Output() proximaPagina = new EventEmitter<void>();
    /** "Editar registro" do menu da linha: abre o documento (a linha em si só seleciona). */
    @Output() abrir = new EventEmitter<VendaDocumentoStruct>();
    @Output() criarModelo = new EventEmitter<VendaDocumentoStruct>();
    @Output() clonar = new EventEmitter<VendaDocumentoStruct>();
    @Output() verHistorico = new EventEmitter<VendaDocumentoStruct>();
    @Output() verOportunidadesCliente = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluir = new EventEmitter<VendaDocumentoStruct>();
    @Output() verOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() criarOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() vincularOportunidade = new EventEmitter<VendaDocumentoStruct>();
    @Output() desvincularOportunidade = new EventEmitter<VendaDocumentoStruct>();

    public readonly tooltipOportunidadeOutroVendedor = TOOLTIP_OPORTUNIDADE_OUTRO_VENDEDOR;
    @Output() lancarFinanceiro = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluirFinanceiro = new EventEmitter<VendaDocumentoStruct>();
    @Output() lancarEstoque = new EventEmitter<VendaDocumentoStruct>();
    @Output() excluirEstoque = new EventEmitter<VendaDocumentoStruct>();
    /** Clique no indicador F: os lançamentos do financeiro, só para consultar. */
    @Output() verFinanceiro = new EventEmitter<VendaDocumentoStruct>();

    public readonly _dataSource = new MatTableDataSource<PvLinhaListaVm>([]);

    /**
     * Ordenação pelo cabeçalho como nas outras listagens do HUB: no cliente, sobre as linhas já
     * carregadas (inclusive as do "Ver mais"); o servidor continua devolvendo por data decrescente.
     * Texto compara sem caixa nem acento; modelo não tem data: fica no fim da ordem decrescente (a padrão) e no
     * início da crescente.
     */
    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._dataSource.sort = sort;
        this._dataSource.sortingDataAccessor = (linha, coluna) => {
            switch (coluna) {
                case 'numero':
                    return linha.documento.numero ?? 0;
                case 'cliente':
                    return normalizarValorOrdenacao(linha.documento.nomePessoaCliente ?? '');
                case 'processo':
                    return normalizarValorOrdenacao(linha.nomeProcesso ?? '');
                case 'etapa':
                    return normalizarValorOrdenacao(linha.nomeEtapa ?? '');
                case 'tags':
                    return normalizarValorOrdenacao((linha.documento.tags ?? []).map(tag => tag.descricao).join(', '));
                case 'responsavel':
                    return normalizarValorOrdenacao(linha.documento.nomeVendedor ?? '');
                case 'data':
                    return this.ehModelo(linha.documento) ? '' : linha.documento.dataEmissao ?? '';
                case 'valor':
                    return linha.documento.valorTotal ?? 0;
                default:
                    return '';
            }
        };
    }

    public readonly displayedColumns: string[] = [
        'select',
        'numero',
        'cliente',
        'processo',
        'etapa',
        'tags',
        'integracoes',
        'responsavel',
        'data',
        'valor',
        'actions',
    ];

    public isTodosSelecionados(): boolean {
        return this._dataSource.data.length > 0 && this.listSelection.selected.length === this._dataSource.data.length;
    }

    /** Indicadores da coluna Integrações, na ordem fixa (E, F), só quando lançadas. */
    public integracoes(documento: VendaDocumentoStruct): { sigla: string; rotulo: string; cor: string; isFinanceiro: boolean }[] {
        const indicadores: { sigla: string; rotulo: string; cor: string; isFinanceiro: boolean }[] = [];
        if (documento.isEstoqueLancado) {
            const estoque = VendaTipoIntegracaoIndicador[VendaTipoIntegracaoEnum.Estoque];
            indicadores.push({ sigla: estoque.sigla, rotulo: 'Estoque lançado', cor: estoque.cor, isFinanceiro: false });
        }
        if (documento.isFinanceiroLancado) {
            const financeiro = VendaTipoIntegracaoIndicador[VendaTipoIntegracaoEnum.Financeiro];
            indicadores.push({ sigla: financeiro.sigla, rotulo: 'Financeiro lançado. Clique para ver os lançamentos.', cor: financeiro.cor, isFinanceiro: true });
        }
        return indicadores;
    }

    /** "#M-02" no modelo, número puro no resto — mesma regra do card do board. */
    public numeroExibido(documento: VendaDocumentoStruct): string {
        return formatarNumeroDocumento(documento.numero, documento.idVendaTipoDocumento);
    }

    /**
     * Modelo é molde, não negociação: a data que o banco guarda é a de criação do molde, e
     * mostrá-la aqui sugeriria um documento com prazo. O card do board já omite; a Lista fazia
     * diferente e o mesmo registro dizia duas coisas.
     */
    public ehModelo(documento: VendaDocumentoStruct): boolean {
        return documento.idVendaTipoDocumento === VendaTipoDocumentoEnum.Modelo;
    }

    /** Só o pedido lança financeiro; o orçamento nunca gera contas (DF5). */
    public ehPedido(linha: PvLinhaListaVm): boolean {
        return linha.documento.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda;
    }

    /**
     * Mesma regra do board (`PvColunaComponent.podeCriarModelo`): orçamento em etapa de FLUXO
     * PRINCIPAL. Duplicar a condição aqui é o preço de a Lista não ter colunas — o que não se
     * duplica é a decisão, que continua sendo do servidor.
     */

    public rotuloClonar(linha: PvLinhaListaVm): string {
        return linha.documento.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? 'Clonar venda' : 'Clonar orçamento';
    }

    public podeCriarModelo(linha: PvLinhaListaVm): boolean {
        if (!this.podeInserir) return false;
        if (linha.documento.idVendaTipoDocumento !== VendaTipoDocumentoEnum.Orcamento) return false;

        return linha.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal;
    }

    /** Marca "#N" do documento vinculado (RX-07), a mesma do card do board. */
    public temMarcaOportunidade(documento: VendaDocumentoStruct): boolean {
        return temMarcaOportunidade(documento);
    }

    public tooltipMarcaOportunidade(documento: VendaDocumentoStruct): string {
        return tooltipMarcaOportunidade(documento);
    }

    /** Itens de oportunidade do menu (D3), com as mesmas regras do card do board. */
    public acoesOportunidade(documento: VendaDocumentoStruct): PvAcoesOportunidadeVm {
        return acoesOportunidade(documento, documento.idPessoaCliente, this.permissoesOportunidade);
    }

    public resumoItens(qtdItens: number): string {
        if (!qtdItens) return 'Sem itens';

        return qtdItens === 1 ? '1 item' : `${qtdItens} itens`;
    }
}
